import { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { TextInput } from '@/components/ui';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import FormField from '@/components/ui/Input/FormField';
import { updateTenant, getProductTypes } from '@/services/tenantService';
import { useCountries } from '@/hooks/useCountries';
import { useNotificationStore } from '@stores/notificationStore';
import { useTranslation } from '@/hooks/useTranslation';
import type { TenantWithUser, UpdateTenantPayload } from '@/types/tenant';
import styles from './ProfileEditForm.module.scss';

// ============================================
// TYPES
// ============================================

interface ProfileEditFormValues {
  organizationName: string;
  mobile: string;
  productTypes: string[];
  firstName: string;
  lastName: string;
  email: string;
}

interface ProfileEditFormProps {
  tenantData: TenantWithUser;
  onSaved: () => void;
  /** HTML id for the <form> — allows external submit buttons via form attribute */
  formId?: string;
  /** Called when mutation pending state changes — lets parent show loading in modal footer */
  onPendingChange?: (pending: boolean) => void;
}

// ============================================
// COMPONENT
// ============================================

function ProfileEditForm({ tenantData, onSaved, formId, onPendingChange }: ProfileEditFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { tenant, user } = tenantData;

  // --- Reference data ---
  const { countries } = useCountries();

  const { data: productTypesData } = useQuery({
    queryKey: ['productTypes'],
    queryFn: () => getProductTypes({ size: 100 }),
  });

  // Country is locked in edit mode — resolve display name from id
  const countryDisplay = useMemo(() => {
    const id = tenant.countryId;
    if (!id) return '-';
    return tenant.country?.name ?? countries.find((c) => c.id === id)?.name ?? id;
  }, [tenant.country?.name, tenant.countryId, countries]);

  const productTypeOptions = useMemo(
    () => (productTypesData?.content ?? []).map((pt) => ({ label: pt.productType, value: pt.id })),
    [productTypesData],
  );

  // --- Form ---
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ProfileEditFormValues>({
    defaultValues: {
      organizationName: tenant.name ?? '',
      mobile: user?.mobile ?? '',
      productTypes: tenant.productTypeIds ?? [],
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
    },
    mode: 'onChange',
  });

  // --- Mutation ---
  const mutation = useMutation({
    mutationFn: (payload: UpdateTenantPayload) => updateTenant(tenant.id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenantCounts'] });
      onSaved();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  // Sync pending state to parent for modal footer
  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const onSubmit = useCallback(
    (data: ProfileEditFormValues) => {
      const payload: UpdateTenantPayload = {
        tenant: {
          name: data.organizationName,
          productTypeIds: data.productTypes,
        },
        user: {
          firstName: data.firstName,
          lastName: data.lastName || undefined,
          email: data.email,
          mobile: data.mobile || undefined,
        },
      };
      mutation.mutate(payload);
    },
    [mutation],
  );

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.formGrid}>
        <TextInput
          label={t('K_LABEL_ORGANISATION_NAME', 'Company Name')}
          isRequired
          placeholder={t('K_PLACEHOLDER_ORGANISATION_NAME', 'Enter company name')}
          error={errors.organizationName?.message}
          {...register('organizationName', {
            required: t('K_VALIDATION_ORGANISATION_NAME_REQUIRED', 'Company name is required'),
            maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
          })}
        />

        <FormField id="subdomain-display" label={t('K_LABEL_SUBDOMAIN', 'Subdomain')}>
          <div className={styles.readOnlyDisplay}>
            {tenant.subdomain ? `${tenant.subdomain}.workpay.com` : '-'}
          </div>
        </FormField>

        <FormField id="country-display" label={t('K_LABEL_COUNTRY', 'Country')}>
          <div className={styles.readOnlyDisplay}>{countryDisplay}</div>
        </FormField>

        <TextInput
          label={t('K_LABEL_MOBILE', 'Mobile')}
          placeholder="+254712345678"
          error={errors.mobile?.message}
          {...register('mobile', {
            pattern: {
              value: /^\+?[0-9]{7,15}$/,
              message: t('K_VALIDATION_PHONE_INVALID', 'Enter a valid phone number (7-15 digits)'),
            },
          })}
        />

        <Controller
          name="productTypes"
          control={control}
          rules={{
            validate: (v) => v.length > 0 || t('K_VALIDATION_PRODUCT_TYPE_REQUIRED', 'At least one product type is required'),
          }}
          render={({ field }) => (
            <MultiSelect
              label={t('K_LABEL_PRODUCT_TYPES', 'Product Types')}
              isRequired
              placeholder={t('K_PLACEHOLDER_PRODUCT_TYPES', 'Select product types')}
              options={productTypeOptions}
              value={field.value}
              onChange={field.onChange}
              error={errors.productTypes?.message}
            />
          )}
        />

        <TextInput
          label={t('K_LABEL_EMAIL', 'Email')}
          isRequired
          type="email"
          placeholder={t('K_PLACEHOLDER_EMAIL', 'Enter email')}
          error={errors.email?.message}
          {...register('email', {
            required: t('K_VALIDATION_EMAIL_REQUIRED', 'Email is required'),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t('K_VALIDATION_EMAIL_PATTERN', 'Enter a valid email address'),
            },
          })}
        />

        <TextInput
          label={t('K_LABEL_FIRST_NAME', 'First Name')}
          isRequired
          placeholder={t('K_PLACEHOLDER_FIRST_NAME', 'Enter first name')}
          error={errors.firstName?.message}
          {...register('firstName', {
            required: t('K_VALIDATION_FIRST_NAME_REQUIRED', 'First name is required'),
            maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
          })}
        />

        <TextInput
          label={t('K_LABEL_LAST_NAME', 'Last Name')}
          placeholder={t('K_PLACEHOLDER_LAST_NAME', 'Enter last name')}
          error={errors.lastName?.message}
          {...register('lastName', {
            maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
          })}
        />
      </div>

    </form>
  );
}

export default ProfileEditForm;
